// Query-led FAQs for operational articles that predate the shared FAQ pipeline.
// Answers stay visible in the article body and are mirrored into FAQPage schema by
// articleEditorialPolicy.js and the prerenderer.

export const ARTICLE_QUERY_FAQS = {
  'human-in-the-loop-authorization-ciba-ai-agents': [
    {
      question: 'what is human-in-the-loop authorization for ai agents',
      answer: '<p>Human-in-the-loop authorization is an approval decision made by an accountable person before an agent performs a consequential action. The approval should identify the actor, agent, action, resource, material parameters, expiry, and policy decision rather than approving a vague conversation.</p>',
    },
    {
      question: 'how does ciba work for ai agent approvals',
      answer: '<p>CIBA-style approval separates the agent-facing request from the person’s authentication and decision channel. The agent submits a bounded action request, the approver reviews it through a trusted channel, and the execution boundary proceeds only after receiving a matching approved result.</p>',
    },
    {
      question: 'what should an ai agent approval be bound to',
      answer: '<p>Bind approval to a fingerprint of the exact action: tool, operation, destination, resource scope, important parameters, agent and workflow version, requester, and expiry. A material change should invalidate the approval and require a new decision.</p>',
    },
    {
      question: 'when should an ai agent require human approval',
      answer: '<p>Require approval when an action is irreversible, moves money or sensitive data, changes access, communicates externally, crosses a policy threshold, or has an uncertain side effect. Routine read-only actions can remain automated when policy and evidence are sufficient.</p>',
    },
  ],
  'ai-agent-incident-response-tabletop-exercise': [
    {
      question: 'how do you run an ai agent incident response tabletop',
      answer: '<p>Run a timed scenario that forces the team to identify the agent owner, contain the narrowest capability, revoke credentials, stop retries, preserve evidence, reconcile downstream effects, communicate, and approve recovery. Record every decision the available evidence could not support.</p>',
    },
    {
      question: 'who should join an ai agent incident tabletop exercise',
      answer: '<p>Include the workflow owner, platform engineering, security, identity, integration owners, privacy or legal, communications, and the owner of each affected downstream system. Agent incidents cross these boundaries more often than they remain inside one service team.</p>',
    },
    {
      question: 'what scenarios should an ai agent tabletop test',
      answer: '<p>Test prompt injection, unauthorized tool use, compromised credentials, ambiguous side effects, repeated execution, missing telemetry, sensitive-data exposure, and a dependency outage. Include at least one case where disabling a shared connector harms another critical workflow.</p>',
    },
    {
      question: 'how do you score an ai agent incident response exercise',
      answer: '<p>Measure time to declare, identify ownership, contain the capability, stop retries, enumerate affected sessions, preserve evidence, reconcile side effects, notify stakeholders, and recover safely. A discussion is not a pass until the technical controls are demonstrated.</p>',
    },
  ],
  'multi-tenant-opentelemetry-ai-agents': [
    {
      question: 'how do you isolate opentelemetry data for multiple ai tenants',
      answer: '<p>Resolve tenant and workspace identity from the authenticated execution context, attach controlled identifiers to telemetry, authorize exports by destination, and enforce separation again in storage and query paths. Do not trust a tenant label supplied by a prompt or request body.</p>',
    },
    {
      question: 'should ai agent traces include a tenant id',
      answer: '<p>Yes, when the identifier is controlled, stable, non-secret, and derived from trusted identity. It enables scoped troubleshooting and cost allocation, but it must not expose customer names or become the only tenant-isolation control.</p>',
    },
    {
      question: 'how do you keep prompts and tool arguments out of telemetry',
      answer: '<p>Default to structural fields such as model, tool name, status, latency, token counts, policy result, and correlation IDs. Capture content only for an explicit purpose, with redaction, separate access, short retention, and tests that detect secrets and sensitive data.</p>',
    },
    {
      question: 'how should you sample ai agent traces',
      answer: '<p>Keep complete records for denied, failed, high-impact, and incident-linked actions while sampling lower-risk successful traffic by a documented policy. Sampling must never create an apparent success by dropping the failure or approval event needed to interpret a run.</p>',
    },
  ],
  'ai-agent-reliability-evidence-chain': [
    {
      question: 'what is an ai agent reliability evidence chain',
      answer: '<p>It is a linked record connecting the request and identity to policy, plan, model and tool execution, approvals, artifacts, failures, recovery, and accepted outcome. The links let an operator reconstruct one run without guessing across disconnected logs.</p>',
    },
    {
      question: 'why is an accuracy score not enough for ai agent reliability',
      answer: '<p>An aggregate score cannot show which version, tool call, credential, approval, retry, or artifact caused one failed outcome. Reliability requires outcome-level evidence about authority, execution, recovery, latency, cost, and whether the business accepted the result.</p>',
    },
    {
      question: 'what records belong in an ai agent evidence chain',
      answer: '<p>Record the initiating actor, tenant and workspace, agent and workflow version, effective policy, plan, model route, tool requests and receipts, approvals, retries, artifacts, terminal status, and accepted business outcome under stable identifiers.</p>',
    },
    {
      question: 'how do you test an ai agent evidence chain',
      answer: '<p>Select one failed or denied workflow and reconstruct it from the outcome back to the requester. The test fails if a reviewer cannot identify authority, effective versions, executed side effects, missing events, and the final reconciliation state.</p>',
    },
  ],
  'mcp-gateway-vs-api-gateway': [
    {
      question: 'what is the difference between an mcp gateway and an api gateway',
      answer: '<p>An API gateway governs service traffic such as routes, tokens, rates, and network policy. An MCP gateway additionally understands agent tools, delegated identity, tool schemas, action scope, approvals, credential resolution, and evidence about why an agent invoked an operation.</p>',
    },
    {
      question: 'do ai agents need both an api gateway and an mcp gateway',
      answer: '<p>Often yes because they control different layers. The API gateway remains useful for network and service policy, while the MCP gateway governs which agent may use which named tool operation, under whose authority, and with what evidence.</p>',
    },
    {
      question: 'is an api gateway enough to secure ai agent tool calls',
      answer: '<p>Not by itself when authorization depends on agent, workspace, delegated purpose, tool semantics, material parameters, or human approval. A conventional gateway can enforce a route and token without understanding whether the planned agent action was permitted.</p>',
    },
    {
      question: 'where should mcp authorization be enforced',
      answer: '<p>Enforce it at the execution boundary before resolving the upstream credential or invoking the tool. Revalidate token audience, actor, tenant, workspace, agent, operation, resource, approval, and policy there, then record the decision and downstream receipt.</p>',
    },
  ],
  'ai-agent-session-replay-incident-response': [
    {
      question: 'what should an ai agent session replay capture',
      answer: '<p>Capture stable run and step IDs, effective agent and workflow versions, model routes, tool arguments and receipts where permitted, approvals, retries, failures, artifacts, policy decisions, timing, cost, and final reconciliation state.</p>',
    },
    {
      question: 'does ai agent session replay require chain of thought',
      answer: '<p>No. Incident replay should use structured decisions and observable events rather than hidden chain-of-thought. Preserve the inputs, plans, policy results, tool activity, approvals, outputs, and outcomes needed to explain what the system did.</p>',
    },
    {
      question: 'how do you protect sensitive data in ai session replay',
      answer: '<p>Collect the minimum content needed, reference source records where possible, redact secrets, separate access to payloads from operational metadata, encrypt retained content, apply purpose-based retention, and record what was omitted.</p>',
    },
    {
      question: 'how does session replay help ai incident response',
      answer: '<p>Replay creates a correlated timeline for containment and reconstruction. It helps responders identify affected sessions, credentials, tools, duplicate effects, missing evidence, and the last trusted state before deciding how to recover.</p>',
    },
  ],
  'secure-mcp-gateway-rollout-plan': [
    {
      question: 'how do you roll out an enterprise mcp gateway',
      answer: '<p>Inventory existing connections, choose one bounded workflow, observe traffic without changing execution, enforce read-only policy first, add approvals for consequential actions, canary a limited population, and expand only after denial and recovery tests pass.</p>',
    },
    {
      question: 'which tools should move behind an mcp gateway first',
      answer: '<p>Start with a useful, well-owned workflow whose operations and side effects are understood. Prefer read-heavy tools with clear identities and rollback paths before payment, access-control, deletion, or broad administrative operations.</p>',
    },
    {
      question: 'should an mcp gateway fail open or fail closed',
      answer: '<p>Fail closed for actions that require authorization, approval, credential resolution, or retained evidence. A separately approved degraded read path may be reasonable, but silent bypass removes the exact control the gateway was introduced to provide.</p>',
    },
    {
      question: 'how do you measure an mcp gateway rollout',
      answer: '<p>Track allowed and denied operations, policy latency, tool success, ambiguous side effects, approval completion, evidence completeness, retries, affected workflows, and recovery time. Volume alone does not show whether the gateway is governing safely.</p>',
    },
  ],
  'ai-agent-incident-response-runbook': [
    {
      question: 'what should an ai agent incident response runbook contain',
      answer: '<p>Define ownership, severity, containment switches, identity and credential revocation, retry control, affected-session discovery, evidence preservation, downstream reconciliation, communications, recovery approval, and permanent regression tests.</p>',
    },
    {
      question: 'what is the first containment step for an ai agent incident',
      answer: '<p>Stop the narrowest harmful capability while preserving evidence. Disable the affected agent, tool assignment, operation, credential, workflow version, or tenant route before taking down shared infrastructure that unrelated workflows still need.</p>',
    },
    {
      question: 'how do you handle an ambiguous ai agent side effect',
      answer: '<p>Stop automatic retries, preserve the idempotency key and request evidence, ask the downstream system for its authoritative state, and reconcile before retrying or rolling back. A timeout does not prove that an action failed.</p>',
    },
    {
      question: 'what evidence should be preserved during an ai agent incident',
      answer: '<p>Preserve identities, effective versions, policy decisions, plans, model routes, tool requests and receipts, approvals, credentials references, retries, artifacts, telemetry gaps, downstream state, containment actions, and recovery decisions.</p>',
    },
  ],
  'ai-agent-observability-opentelemetry': [
    {
      question: 'what should you trace for ai agent observability',
      answer: '<p>Trace runs and steps, model calls, tool operations, policy decisions, approvals, retries, failures, latency, token usage, cost, artifacts, and terminal outcomes with stable correlation identifiers.</p>',
    },
    {
      question: 'can opentelemetry trace ai agents',
      answer: '<p>Yes. OpenTelemetry can carry correlated spans, metrics, logs, and GenAI attributes across model, tool, and workflow boundaries. You still need an application-level evidence model for authority, approvals, and accepted outcomes.</p>',
    },
    {
      question: 'should prompts be stored in opentelemetry traces',
      answer: '<p>Not by default. Prefer structural metadata and references; capture prompt or tool content only for a documented purpose with redaction, restricted access, encryption, short retention, and secret-detection tests.</p>',
    },
    {
      question: 'is ai observability the same as an audit trail',
      answer: '<p>No. Observability explains system behavior and health, while an audit trail proves identity, authority, decisions, execution, and outcomes. They can share correlation IDs and storage components without becoming the same record.</p>',
    },
  ],
};

export function getArticleQueryFaqs(slug) {
  return ARTICLE_QUERY_FAQS[slug] || [];
}
